import axios from 'axios';

import { getEnvUrl } from './getEnvUrl';

/**
 * Funnel → PWA autologin (handoff code).
 *
 * The funnel finishes with the user already authenticated (the backend returns
 * an `accessToken`). Instead of shipping that token to the app, the funnel
 * exchanges it for a one-time, 5-minute handoff code and passes the code to the
 * PWA in the URL *fragment* (`#handoff=<code>`). The PWA redeems the code for
 * its own tokens and logs the user in without an email code.
 *
 * Contract: `POST {API_BASE}/auth/handoff` with `Authorization: Bearer <token>`
 * → `201 { code, expiresIn }` (foodzee-api `session-exchange.service.ts`,
 * Swagger tag "Auth"). `API_BASE` is the same base the funnel already uses for
 * its authed calls (`getEnvUrl().apiBaseUrl` — `public-api.hairio.app` in
 * prod). The `APP_URL` is provided by the caller (`getPwaUrlForOnboarding()`).
 *
 * Autologin is best-effort: on any non-2xx / network error we return null and
 * the caller opens the app without a fragment, dropping the user into the
 * normal Welcome/OTP flow.
 */

type HandoffResponse = {
  code: string;
  expiresIn: number;
};

/**
 * Exchange the funnel's accessToken for a one-time handoff code.
 * Request the code only on the actual "open app" transition, not ahead of time
 * — the code lives 5 minutes and is single-use. Retries / repeat calls are
 * safe: the endpoint issues a fresh code and invalidates the previous one.
 *
 * @returns the handoff code, or null if it could not be obtained.
 */
export const requestHandoffCode = async (
  accessToken: string,
): Promise<string | null> => {
  if (!accessToken) {
    return null;
  }

  try {
    const { apiBaseUrl } = getEnvUrl();
    const { data } = await axios.post<HandoffResponse>(
      `${apiBaseUrl}/auth/handoff`,
      undefined,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return data?.code || null;
  } catch (_) {
    return null;
  }
};

/**
 * Build the PWA URL for autologin. With a code, the handoff goes in the URL
 * *fragment* (`#handoff=<code>`) — strictly the fragment, never a query param,
 * so it never reaches the server (CDN logs / Referer). Without a code, returns
 * the bare app URL so the user just hits the normal login.
 */
export const buildAppUrlWithHandoff = (appUrl: string, code: string | null): string =>
  code ? `${appUrl}/#handoff=${code}` : appUrl;
